import { getGrowthBookConfigLoader } from '@doxyme/config';
import { useGetPatientFeatureEntitlementQuery } from '../../services/entitlements/operations.generated';
import { useAppSelector } from '../../hooks/useReduxToolkit';
import { getWaitingRoomAdminSubscriptionType } from '../../features/waitingRoom/waitingRoom.selectors';
import { RoomForPatient, UserSubscriptionType } from '../../services';
import { useService } from '../../hooks';
import { FronteggFeatureKeys } from '../../context/userEntitlementsContext.types';
import { getIsGraphqlRoomQueriesEnabled } from '../../utils/isGraphqlRoomQueriesEnabled';

type UsePatientPaidStatusReturn = {
  isPaidUser: boolean;
  roomAdminSubscriptionType: UserSubscriptionType;
  freeOrPaid: 'free' | 'paid';
};

export const usePatientPaidStatus = (
  room?: RoomForPatient | null,
): UsePatientPaidStatusReturn => {
  const isProvider = useService('$context') === 'provider';

  const isFronteggEntitlementsEnabled =
    getGrowthBookConfigLoader().getOptional(
      'feat_prod_2316_frontegg_entitlements_enabled',
    ) === true;
  const legacySubscriptionType = useAppSelector(
    getWaitingRoomAdminSubscriptionType,
  );
  const isGraphqlRoomQueriesEnabled = getIsGraphqlRoomQueriesEnabled();

  const { currentData: patientFeatureEntitlement } =
    useGetPatientFeatureEntitlementQuery(
      {
        input: {
          featureKey: FronteggFeatureKeys.ProPlan,
          roomSlug: room?.slug as string,
        },
      },
      {
        skip:
          !room?.slug ||
          !isFronteggEntitlementsEnabled ||
          isGraphqlRoomQueriesEnabled ||
          isProvider,
      },
    );

  if (isProvider) {
    return {
      isPaidUser: false,
      roomAdminSubscriptionType: UserSubscriptionType.Free,
      freeOrPaid: 'free',
    };
  }

  // Use Frontegg entitlements if enabled, otherwise fall back to legacy system
  if (isFronteggEntitlementsEnabled) {
    // Handle when room.owner is null, it is shared room
    // Handle premium plan
    const isPaidUser = isGraphqlRoomQueriesEnabled
      ? room?.owner?.subscriptionType === UserSubscriptionType.Pro
      : Boolean(
          patientFeatureEntitlement?.Entitlements.getPatientFeatureEntitlement
            ?.isEntitled,
        );

    return {
      isPaidUser,
      roomAdminSubscriptionType: isPaidUser
        ? UserSubscriptionType.Pro
        : UserSubscriptionType.Free,
      freeOrPaid: isPaidUser ? 'paid' : 'free',
    };
  }

  // Legacy system
  const isPaidUser =
    legacySubscriptionType !== undefined &&
    legacySubscriptionType !== UserSubscriptionType.Free;

  return {
    isPaidUser,
    roomAdminSubscriptionType:
      legacySubscriptionType || UserSubscriptionType.Free,
    freeOrPaid: isPaidUser ? 'paid' : 'free',
  };
};
